import {
  ConversationTypeEnum,
  IContactInfo,
  ITokenResponse,
  KEY_SESSION_TOKEN,
  IConversationAuthCode,
  IConversationAuthClient,
  Restorable,
} from "@auvious/integrations";
import { ErrorCode } from "..";
import { sessionStore } from "@auvious/utils";
import { CommonService } from "../../services/common.service";
import { HandledError } from "../HandledError";
import { AuviousCommon, Util } from "@auvious/common";
import { AppConfigService } from "../../services/app.config.service";
import ky from "ky";
import { Base64 } from "js-base64";
import { RTCService } from "../../services/rtc.service";
import { OAuth2TokenHelper } from "../../services/conversation.auth.service";
import { AuviousRtc } from "@auvious/rtc";

const TOKEN_CLIENT_ID = "talkdesk-guest";
const KEY_SESSION_ID = "auvious.session.id";

interface IDCAuthCode extends IConversationAuthCode {
  touchpointId: string;
  idempotentId?: string;
  subject: string;
  contactEmail: string;
}

export class DigitalConnectAuthClient
  extends Restorable
  implements IConversationAuthClient
{
  private _token: ITokenResponse;
  private _authCode: IDCAuthCode;
  private _commonClient: AuviousCommon;
  private _rtcClient: AuviousRtc;

  constructor(common: AuviousCommon) {
    super([KEY_SESSION_ID]);
    this._commonClient = common;
  }

  getRestorableState() {
    return this.removeNullUndefined({
      ...super.getRestorableState(),
      [KEY_SESSION_TOKEN]: OAuth2TokenHelper.getToken(KEY_SESSION_TOKEN),
    });
  }

  restoreState(state: object) {
    if (KEY_SESSION_TOKEN in state)
      OAuth2TokenHelper.saveToken(KEY_SESSION_TOKEN, state[KEY_SESSION_TOKEN]);
    super.restoreState(state);
  }

  public getAuthCode(
    contact: IContactInfo,
    conversationType: ConversationTypeEnum
  ): IDCAuthCode {
    switch (AppConfigService.chatMode) {
      case "talkdesk-digital-connect":
        const customProperties = {
          ...contact,
          conversationType,
        };

        const payload: IDCAuthCode = {
          applicationId: AppConfigService.applicationId,
          touchpointId: AppConfigService.dcTouchpointId,
          subject: contact.subject,
          contactEmail: contact.email,
          customProperties,
        };
        return payload;
    }
  }

  public async register(): Promise<{ conversationId: string }> {
    try {
      await ky.post("api/digital-connect/talkdesk/conversations/accept", {
        prefixUrl: AppConfigService.auviousBaseUrl,
        json: { conversationId: this._token.conversation_id },
        headers: {
          Authorization: `Bearer ${this._token.access_token}`,
        },
      });
      return { conversationId: this._token.conversation_id };
    } catch (ex) {
      throw new HandledError(ex, ErrorCode.DIGITAL_CONNECT_API_ERROR);
    }
  }

  public async authenticate(code: IDCAuthCode): Promise<ITokenResponse> {
    try {
      this._token = OAuth2TokenHelper.getToken(KEY_SESSION_TOKEN);
      this._authCode = code;

      // if we have a sessionid it means we have an existing conversation
      const sessionId = sessionStore.getItem(KEY_SESSION_ID);

      if (this._token) {
        if (OAuth2TokenHelper.isTokenExpired(this._token)) {
          this.clearAccessToken();
          // reauthenticate
          this._token = await this.getAccessToken(sessionId)();
          await this._commonClient.loginTemplate(
            () => Promise.resolve(this._token),
            this.getRefreshToken()
          );
        } else {
          // if we have a refresh token, let's try it now
          if (this._token.refresh_token) {
            try {
              this._token = await this.getRefreshToken()(
                this._token.refresh_token
              );
            } catch (error) {
              // refresh failed, let's try get a new one
              this.clearAccessToken();
              this._token = await this.getAccessToken(sessionId)();
            }

            await this._commonClient.loginTemplate(
              () => Promise.resolve(this._token),
              this.getRefreshToken()
            );
          } else {
            await this._commonClient.loginTemplate(
              () => Promise.resolve(this._token),
              this.getRefreshToken()
            );
          }
        }
      } else {
        this._token = await this.getAccessToken()();
        await this._commonClient.loginTemplate(
          () => Promise.resolve(this._token),
          this.getRefreshToken()
        );
      }

      // we need RTC to register to an endpoint
      this._rtcClient = await RTCService.createInstance(this._commonClient);

      return this._token;
    } catch (ex) {
      throw new HandledError(
        ex.response ? ex.response : ex,
        ErrorCode.AUTHENTICATION_ERROR
      );
    }
  }

  public async logout() {
    await RTCService.logout(this._rtcClient);
    this._rtcClient = undefined;
    await CommonService.logout(this._commonClient);
    sessionStore.removeItem(KEY_SESSION_ID);
    this.clearAccessToken();
  }

  /** private  */

  private clearAccessToken() {
    OAuth2TokenHelper.removeToken(KEY_SESSION_TOKEN);
    this._token = undefined;
  }

  private getAccessToken(
    sessionId?: string
  ): (refreshToken?: string) => Promise<ITokenResponse> {
    return async (): Promise<ITokenResponse> => {
      const idempotentId = sessionId || Util.uuidgen();

      const body = new URLSearchParams();
      body.append("grant_type", "password");
      body.append("client_id", TOKEN_CLIENT_ID);
      body.append(
        "payload",
        Base64.encode(JSON.stringify({ ...this._authCode, idempotentId }))
      );

      const token: ITokenResponse = await ky
        .post("security/oauth/token", {
          prefixUrl: AppConfigService.auviousBaseUrl,
          body,
        })
        .json();

      // store session id
      sessionStore.setItem(KEY_SESSION_ID, idempotentId);
      OAuth2TokenHelper.saveToken(KEY_SESSION_TOKEN, token);

      return token;
    };
  }

  private getRefreshToken(): (refreshToken: string) => Promise<ITokenResponse> {
    return async (refreshToken: string): Promise<ITokenResponse> => {
      const body = new URLSearchParams();
      body.set("client_id", TOKEN_CLIENT_ID);
      body.set("grant_type", "refresh_token");
      body.set("refresh_token", refreshToken);

      const token: ITokenResponse = await ky
        .post("security/oauth/token", {
          prefixUrl: AppConfigService.auviousBaseUrl,
          body,
        })
        .json();

      // update stored token
      OAuth2TokenHelper.saveToken(KEY_SESSION_TOKEN, token);

      // update token
      this._token = token;

      return token;
    };
  }
}
